import { DateTime } from 'luxon';
import { IUploadDocument } from '../../types/order/documents/upload/interfaces';
import { IMetadata, IStandardDocument, IStandardDocumentResponse } from '../../types/order/documents/shared/interfaces';
import { Logger } from '../../ui-toolkit/logging/LoggerService';
import { APIFactory } from '../../ui-toolkit/rest/APIFactory';
import { baseUrlService } from '../../ui-toolkit/rest/BaseUrlService';

export const getDocumentsByOrderAsync = async (id: string) => {
  try {
    const api = APIFactory(baseUrlService.ApiLocation);
    const result = await api.get(`documents/v5/${id}/documents`);
    return result.data.map(convertGetDocumentResponseToStandardDocument);
  } catch (e: any) {
    Logger.error('Document List Error', e);
    throw e;
  }
};

interface IUploadDocumentMetadata {
  good_through_date?: string | null;
}

const convertMetadataToRequestDTO = (data: IMetadata): IUploadDocumentMetadata => {
  const { good_through_date, ...rest } = data;
  const dateToUse =
    good_through_date && DateTime.fromISO(good_through_date).isValid
      ? DateTime.fromISO(good_through_date).toISO()
      : undefined;
  return {
    ...rest,
    good_through_date: dateToUse,
  };
};

const convertUploadDocumentToCreateRequest = (document: IUploadDocument) => {
  let data = new FormData();
  data.append('file', document.file);
  data.append('file_name', document.file.name);
  if (document.metadata) {
    data.append('json', JSON.stringify(convertMetadataToRequestDTO(document.metadata)));
  }
  return data;
};

const generateCreateUrl = ({ order_id, category, isRepeatable }: IUploadDocument) =>
  `/documents/v4/${order_id}/${category}/${!isRepeatable ? 'versioned' : 'unversioned'}/upload`;

export const createDocument = async (uploadDocument: IUploadDocument) => {
  try {
    const api = APIFactory(baseUrlService.ApiLocation);
    const data = convertUploadDocumentToCreateRequest(uploadDocument);
    const result = await api.post(generateCreateUrl(uploadDocument), data);
    return result.data;
  } catch (e: any) {
    Logger.error('Document Upload Error', e);
    throw e;
  }
};

const convertGetDocumentResponseToStandardDocument = ({
  created_at,
  file_updated_at,
  metadata,
  updated_at,
  ...rest
}: IStandardDocumentResponse) => ({
  ...rest,
  created_at: DateTime.fromISO(created_at).toJSDate(),
  file_updated_at: DateTime.fromISO(file_updated_at).toJSDate(),
  metadata,
  updated_at: DateTime.fromISO(updated_at).toJSDate(),
});

export const getDocument = async (id: string): Promise<IStandardDocument> => {
  try {
    const api = APIFactory(baseUrlService.ApiLocation);
    const result = await api.get<IStandardDocumentResponse>(`/documents/v5/${id}`);
    return convertGetDocumentResponseToStandardDocument(result.data);
  } catch (e: any) {
    Logger.error('Document Load Error', e);
    throw e;
  }
};

export const updateDocumentMetadata = async (id: string, metadata: IMetadata) => {
  try {
    const api = APIFactory(baseUrlService.ApiLocation);
    const result = await api.put(`/documents/v5/${id}`, metadata);
    return result.data;
  } catch (e: any) {
    Logger.error('Document Update Error', e);
    throw e;
  }
};
